import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { ArrowRight, ArrowDown, ArrowLeft, ChevronRight, X } from 'lucide-react';
import { caseStudies } from '@/data/caseStudiesData';

const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } },
};
const stagger = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.12 } },
};

/* ── Animated Number ── */
function AnimNum({ target, suffix = '' }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-50px' });
  const [val, setVal] = useState(0);
  const num = parseFloat(target.replace(/[^0-9.]/g, ''));
  useEffect(() => {
    if (!inView) return;
    let s = 0;
    const dur = 2200, step = Math.max(0.1, num / (dur / 16));
    const t = setInterval(() => {
      s += step;
      if (s >= num) { setVal(num); clearInterval(t); } else setVal(s);
    }, 16);
    return () => clearInterval(t);
  }, [inView, num]);
  const display = num >= 1000 ? (val / (num >= 1e9 ? 1e9 : num >= 1e6 ? 1e6 : 1e3)).toFixed(num >= 10 ? 0 : 1) : val.toFixed(num < 10 ? 0 : 0);
  const unit = target.replace(/[0-9.,]/g, '').replace('+', '');
  return <span ref={ref}>{display}{unit}{target.includes('+') ? '+' : ''}</span>;
}

/* ── Sticky Nav ── */
function StickyNav({ sections, activeId }) {
  return (
    <div className="fixed right-8 top-1/2 -translate-y-1/2 z-40 hidden xl:flex flex-col gap-3" data-testid="sticky-nav">
      {sections.map((s) => (
        <a
          key={s.id}
          href={`#${s.id}`}
          className={`text-[10px] tracking-widest uppercase text-right transition-all duration-300 ${activeId === s.id ? 'text-foreground font-semibold' : 'text-muted-foreground/50 hover:text-muted-foreground'}`}
        >
          {s.label}
        </a>
      ))}
    </div>
  );
}

/* ── Service Chip ── */
function ServiceChip({ label, delay }) {
  return (
    <motion.span
      initial={{ opacity: 0, scale: 0.8 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay }}
      className="px-5 py-2.5 text-xs tracking-widest uppercase border border-border rounded-full hover:bg-butter hover:border-butter transition-colors duration-300 cursor-default"
    >
      {label}
    </motion.span>
  );
}

/* ── Timeline Step ── */
function TimelineStep({ step, index, total }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      className="flex flex-col items-center text-center relative"
    >
      <div className="w-12 h-12 rounded-full bg-butter flex items-center justify-center mb-3 relative z-10">
        <span className="font-serif text-sm font-medium">{String(index + 1).padStart(2, '0')}</span>
      </div>
      <p className="text-sm font-medium tracking-wide">{step}</p>
      {index < total - 1 && (
        <ChevronRight className="h-4 w-4 text-muted-foreground rotate-90 mt-3 md:hidden" />
      )}
    </motion.div>
  );
}

/* ── Metric Card ── */
function MetricCard({ value, label, delay }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-30px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay }}
      className="text-center p-6"
    >
      <div className="font-serif text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight mb-2">
        <AnimNum target={value} />
      </div>
      <p className="text-sm tracking-widest uppercase text-muted-foreground">{label}</p>
    </motion.div>
  );
}

/* ── Media Logo ── */
function MediaLogo({ name, index }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.08 }}
      className="flex-shrink-0 px-8 py-4 border border-border/50 backdrop-blur-sm bg-background/5 rounded-sm"
    >
      <span className="font-serif text-lg md:text-xl font-medium text-background/80 whitespace-nowrap">{name}</span>
    </motion.div>
  );
}

const NAV_SECTIONS = [
  { id: 'mb-overview', label: 'Overview' },
  { id: 'mb-challenge', label: 'Challenge' },
  { id: 'mb-strategy', label: 'Strategy' },
  { id: 'mb-execution', label: 'Execution' },
  { id: 'mb-results', label: 'Results' },
  { id: 'mb-coverage', label: 'Coverage' },
  { id: 'mb-next', label: 'Next Project' },
];

const SERVICES = ['Creative Direction', 'Campaign Strategy', 'Brand Development', 'Creative Concepting', 'Photography', 'Graphic Design', 'Digital Campaign Creative', 'Social Content', 'Public Awareness Campaign'];

const TIMELINE = ['Research', 'Audience Insights', 'Campaign Strategy', 'Creative Development', 'Content Production', 'Media Rollout', 'Optimization'];

const BREAKUP_METRICS = [
  { value: '6.5B+', label: 'Impressions' },
  { value: '674K+', label: 'Website Visits' },
  { value: '123K+', label: 'Link Clicks' },
  { value: '2B+', label: 'Video Views' },
];

const TAKECARE_METRICS = [
  { value: '4.3M+', label: 'Impressions' },
  { value: '17.9K', label: 'Website Visits' },
  { value: '4091', label: 'Link Clicks' },
  { value: '200K', label: 'Video Views' },
];

const MEDIA = ['The New York Times', 'Bloomberg', 'NBC', 'Good Morning America', 'Miami Herald', 'New York Post', 'Le Figaro', 'The Wall Street Journal', 'People'];

const DELIVERABLES = ['Creative Direction', 'Campaign Strategy', 'Creative Concept Development', 'Photography Direction', 'Graphic Design', 'Digital Advertising Creative', 'Cross-platform Content Production', 'Public Awareness Storytelling'];

/* ── Miami Beach Gallery Slideshow ── */
function MBGallery() {
  const study = caseStudies.find(s => s.slug === 'city-of-miami-beach');
  const images = study ? [study.heroImage, ...(study.galleryImages || [])] : [];
  const [idx, setIdx] = useState(0);
  const [lightbox, setLightbox] = useState(false);

  useEffect(() => {
    if (!lightbox) return;
    const h = e => { if (e.key==='Escape') setLightbox(false); if (e.key==='ArrowRight') setIdx(p=>(p+1)%images.length); if (e.key==='ArrowLeft') setIdx(p=>(p-1+images.length)%images.length); };
    window.addEventListener('keydown', h); return () => window.removeEventListener('keydown', h);
  }, [lightbox, images.length]);

  if (images.length <= 1) return null;

  return (
    <>
      <div className="border-t border-border" />
      <section className="py-28 md:py-36" data-testid="mb-gallery">
        <div className="container-main">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="mb-12">
            <motion.p variants={fadeInUp} className="text-xs tracking-widest uppercase text-muted-foreground mb-4">Campaign Assets</motion.p>
            <motion.h2 variants={fadeInUp} className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium">Selected creative.</motion.h2>
          </motion.div>

          <div className="max-w-5xl mx-auto mb-6">
            <div className="relative bg-white border border-border">
              <AnimatePresence mode="wait">
                <motion.div key={idx} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }} className="cursor-pointer" onClick={() => setLightbox(true)}>
                  <img src={images[idx]} alt={`Campaign asset ${idx+1}`} className="w-full h-[55vh] md:h-[65vh] object-contain mx-auto" />
                </motion.div>
              </AnimatePresence>
            </div>
            <div className="flex items-center justify-between mt-4">
              <button onClick={() => setIdx(p=>(p-1+images.length)%images.length)} className="flex items-center gap-2 text-muted-foreground hover:text-butter transition-colors"><ArrowLeft className="h-5 w-5" /> Previous</button>
              <span className="text-sm text-muted-foreground font-medium tracking-widest">{idx+1} / {images.length}</span>
              <button onClick={() => setIdx(p=>(p+1)%images.length)} className="flex items-center gap-2 text-muted-foreground hover:text-butter transition-colors">Next <ArrowRight className="h-5 w-5" /></button>
            </div>
          </div>

          <div className="flex gap-2 justify-center flex-wrap max-w-5xl mx-auto">
            {images.map((img, i) => (
              <button key={i} onClick={() => setIdx(i)} className={`w-16 h-16 overflow-hidden border transition-colors ${idx===i?'border-butter':'border-border hover:border-butter/50'}`}>
                <img src={img} alt="" className="w-full h-full object-cover" loading="lazy" />
              </button>
            ))}
          </div>
        </div>
      </section>

      <AnimatePresence>
        {lightbox && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center" onClick={() => setLightbox(false)}>
            <button onClick={() => setLightbox(false)} className="absolute top-6 right-6 text-white/60 hover:text-white z-50"><X className="h-8 w-8" /></button>
            <button onClick={e => { e.stopPropagation(); setIdx(p=>(p-1+images.length)%images.length); }} className="absolute left-6 text-white/60 hover:text-white z-50"><ArrowLeft className="h-8 w-8" /></button>
            <button onClick={e => { e.stopPropagation(); setIdx(p=>(p+1)%images.length); }} className="absolute right-6 text-white/60 hover:text-white z-50"><ArrowRight className="h-8 w-8" /></button>
            <img src={images[idx]} alt="" className="max-w-[90vw] max-h-[85vh] object-contain" onClick={e => e.stopPropagation()} />
            <span className="absolute bottom-6 text-white/40 text-sm tracking-widest">{idx+1} / {images.length}</span>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export default function MiamiBeachPage() {
  const [activeSection, setActiveSection] = useState('mb-overview');
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const heroY = useTransform(scrollYProgress, [0, 1], ['0%', '40%']);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.6], [1, 0]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => { if (e.isIntersecting) setActiveSection(e.target.id); });
      },
      { rootMargin: '-40% 0px -40% 0px' }
    );
    NAV_SECTIONS.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  return (
    <div className="pt-32" data-testid="miami-beach-page">
      <StickyNav sections={NAV_SECTIONS} activeId={activeSection} />

      {/* ═══════ HERO ═══════ */}
      <section ref={heroRef} className="relative min-h-[85vh] flex items-center justify-center overflow-hidden bg-foreground" data-testid="mb-hero">
        <motion.div style={{ y: heroY }} className="absolute inset-0 z-0">
          <img src="/images/mb-hero.webp" alt="City of Miami Beach" className="w-full h-full object-cover object-top opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground via-foreground/60 to-foreground/40" />
        </motion.div>
        <motion.div style={{ opacity: heroOpacity }} className="relative z-10 text-center px-6 max-w-5xl mx-auto">
          <motion.div initial="hidden" animate="visible" variants={stagger}>
            <motion.p variants={fadeInUp} className="text-xs tracking-[0.4em] uppercase text-butter mb-6">Featured Case Study</motion.p>
            <motion.h1 variants={fadeInUp} className="font-serif text-5xl sm:text-6xl lg:text-8xl font-medium text-background leading-[1.05] mb-6">
              City of Miami Beach
            </motion.h1>
            <motion.div variants={fadeInUp} className="flex items-center justify-center gap-3 mb-8 flex-wrap">
              {['Creative Direction', 'Campaign Strategy', 'Public Awareness'].map((s, i) => (
                <span key={i} className="text-xs tracking-widest uppercase text-background/50">
                  {i > 0 && <span className="mr-3">•</span>}{s}
                </span>
              ))}
            </motion.div>
            <motion.p variants={fadeInUp} className="text-background/60 text-base md:text-lg max-w-2xl mx-auto leading-relaxed mb-12">
              Transforming one of America's biggest public safety challenges into a campaign that captured worldwide attention.
            </motion.p>
            <motion.a
              variants={fadeInUp}
              href="#mb-overview"
              className="inline-flex items-center gap-2 text-butter text-sm tracking-widest uppercase hover:text-butter-dark transition-colors"
            >
              View Full Case Study
              <motion.span animate={{ y: [0, 6, 0] }} transition={{ duration: 2, repeat: Infinity }}>
                <ArrowDown className="h-4 w-4" />
              </motion.span>
            </motion.a>
          </motion.div>
        </motion.div>
      </section>

      {/* ═══════ OVERVIEW ═══════ */}
      <section id="mb-overview" className="py-28 md:py-36" data-testid="mb-overview">
        <div className="container-main">
          <div className="max-w-5xl mx-auto">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-100px' }} variants={stagger}>
              <motion.p variants={fadeInUp} className="text-xs tracking-widest uppercase text-muted-foreground mb-6">Overview</motion.p>
              <motion.h2 variants={fadeInUp} className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium leading-tight mb-10">
                The City of Miami Beach sought to fundamentally change the narrative surrounding Spring Break.
              </motion.h2>
              <motion.p variants={fadeInUp} className="text-muted-foreground text-base md:text-lg leading-relaxed max-w-3xl">
                After years of overcrowding, public safety concerns, and ineffective awareness campaigns, the objective was to create a bold, culturally relevant campaign capable of changing visitor behavior while generating national awareness before travelers arrived.
              </motion.p>
            </motion.div>

            {/* Services */}
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="mt-16">
              <motion.p variants={fadeInUp} className="text-xs tracking-widest uppercase text-muted-foreground mb-6">Services</motion.p>
              <div className="flex flex-wrap gap-3">
                {SERVICES.map((s, i) => (
                  <ServiceChip key={s} label={s} delay={i * 0.06} />
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <div className="border-t border-border" />

      {/* ═══════ CHALLENGE ═══════ */}
      <section id="mb-challenge" className="py-28 md:py-36" data-testid="mb-challenge">
        <div className="container-main">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 md:gap-24 max-w-6xl mx-auto items-start">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
              <motion.p variants={fadeInUp} className="text-xs tracking-widest uppercase text-muted-foreground mb-4">The Challenge</motion.p>
              <motion.h2 variants={fadeInUp} className="font-serif text-4xl sm:text-5xl lg:text-6xl font-medium leading-[1.08]">
                Breaking through the noise.
              </motion.h2>
            </motion.div>
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="space-y-6">
              <motion.p variants={fadeInUp} className="text-muted-foreground text-base md:text-lg leading-relaxed">
                Previous campaigns struggled to influence visitor behavior or break through the media noise surrounding Spring Break. The campaign needed to:
              </motion.p>
              <motion.ul variants={fadeInUp} className="space-y-4">
                {[
                  'Shift public perception of Miami Beach during Spring Break',
                  'Reach visitors before arrival through digital-first channels',
                  'Clearly communicate new safety measures and enforcement',
                  'Generate national and international conversation',
                  'Increase awareness through culturally relevant storytelling',
                ].map((item, i) => (
                  <motion.li
                    key={i}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.3 + i * 0.1 }}
                    className="flex items-start gap-3"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-butter mt-2.5 flex-shrink-0" />
                    <span className="text-foreground text-sm md:text-base leading-relaxed">{item}</span>
                  </motion.li>
                ))}
              </motion.ul>
            </motion.div>
          </div>
        </div>
      </section>

      <div className="border-t border-border" />

      {/* ═══════ STRATEGY TIMELINE ═══════ */}
      <section id="mb-strategy" className="py-28 md:py-36" data-testid="mb-strategy">
        <div className="container-main">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-20">
            <motion.p variants={fadeInUp} className="text-xs tracking-widest uppercase text-muted-foreground mb-4">Strategy</motion.p>
            <motion.h2 variants={fadeInUp} className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium">
              From research to results.
            </motion.h2>
          </motion.div>

          <div className="relative max-w-5xl mx-auto">
            <div className="hidden md:block absolute top-6 left-0 right-0 h-px bg-border" />
            <div className="grid grid-cols-2 md:grid-cols-7 gap-6 md:gap-3">
              {TIMELINE.map((step, i) => (
                <TimelineStep key={step} step={step} index={i} total={TIMELINE.length} />
              ))}
            </div>
          </div>
        </div>
      </section>

      <div className="border-t border-border" />

      {/* ═══════ CREATIVE EXECUTION ═══════ */}
      <section id="mb-execution" className="py-28 md:py-36 bg-secondary" data-testid="mb-execution">
        <div className="container-main">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="mb-16">
            <motion.p variants={fadeInUp} className="text-xs tracking-widest uppercase text-muted-foreground mb-4">Creative Execution</motion.p>
            <motion.h2 variants={fadeInUp} className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium mb-6">
              Breakup Campaign & Take Care Of Our City
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-muted-foreground text-base md:text-lg leading-relaxed max-w-3xl">
              We managed a multimedia advertising plan including social media placements, web banner ads, public service announcements, and digital campaign creative designed to reach spring breakers and tourists through every available channel.
            </motion.p>
          </motion.div>

          {/* Execution cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              { title: 'Social Media Ads', desc: 'Platform-native content designed for Instagram, TikTok, Facebook and Twitter to intercept audiences before travel.' },
              { title: 'Digital Campaign', desc: 'Web banners, display ads, and retargeting creative deployed across major ad networks and travel platforms.' },
              { title: 'Public Service Announcements', desc: 'PSA creative featuring locals as the voice of safety messaging, distributed across broadcast and digital channels.' },
              { title: 'Campaign Photography', desc: 'Original photography capturing the campaign\'s bold visual identity and Miami Beach\'s cultural energy.' },
              { title: 'Video Production', desc: 'Short-form and long-form video content that generated over 2 billion views across platforms.' },
              { title: 'Website & Landing Pages', desc: 'Dedicated campaign microsites driving 674K+ visits with clear safety messaging and visitor information.' },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08, duration: 0.6 }}
                className="p-8 bg-background border border-border hover:border-butter/50 transition-all duration-500 group"
              >
                <h3 className="font-serif text-lg font-medium mb-3 group-hover:text-butter-dark transition-colors duration-300">{item.title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ RESULTS ═══════ */}
      <section id="mb-results" className="py-28 md:py-36" data-testid="mb-results">
        <div className="container-main">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-20">
            <motion.p variants={fadeInUp} className="text-xs tracking-widest uppercase text-muted-foreground mb-4">The Results</motion.p>
            <motion.h2 variants={fadeInUp} className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium">
              Numbers that speak for themselves.
            </motion.h2>
          </motion.div>

          {/* Breaking Up With Spring Break */}
          <div className="max-w-5xl mx-auto mb-20">
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="border border-border p-10 md:p-14"
            >
              <h3 className="font-serif text-2xl md:text-3xl font-medium mb-2 text-center">Breaking Up With Spring Break</h3>
              <p className="text-muted-foreground text-sm text-center mb-12 tracking-wide">Primary Campaign</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                {BREAKUP_METRICS.map((m, i) => (
                  <MetricCard key={m.label} value={m.value} label={m.label} delay={i * 0.12} />
                ))}
              </div>
            </motion.div>
          </div>

          {/* Take Care Of Our City */}
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="border border-border p-10 md:p-14"
            >
              <h3 className="font-serif text-2xl md:text-3xl font-medium mb-2 text-center">Take Care Of Our City</h3>
              <p className="text-muted-foreground text-sm text-center mb-12 tracking-wide">Supporting Campaign</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                {TAKECARE_METRICS.map((m, i) => (
                  <MetricCard key={m.label} value={m.value} label={m.label} delay={i * 0.12} />
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══════ GLOBAL IMPACT / COVERAGE ═══════ */}
      <section id="mb-coverage" className="py-28 md:py-36 bg-foreground text-background" data-testid="mb-coverage">
        <div className="container-main">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-8">
            <motion.p variants={fadeInUp} className="text-xs tracking-widest uppercase text-background/40 mb-4">Global Impact</motion.p>
            <motion.h2 variants={fadeInUp} className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium text-background mb-8">
              When strategy becomes culture.
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-background/50 text-base md:text-lg leading-relaxed max-w-3xl mx-auto mb-16">
              The campaign became one of the most talked-about public awareness initiatives surrounding Spring Break, generating extensive national and international media attention while helping redefine how government campaigns engage modern audiences.
            </motion.p>
          </motion.div>

          {/* Featured In - Scrolling logos */}
          <div className="mb-20">
            <p className="text-xs tracking-widest uppercase text-background/30 text-center mb-8">Featured In</p>
            <div className="overflow-hidden">
              <motion.div
                animate={{ x: ['0%', '-50%'] }}
                transition={{ duration: 30, repeat: Infinity, ease: 'linear' }}
                className="flex gap-4"
              >
                {[...MEDIA, ...MEDIA].map((name, i) => (
                  <MediaLogo key={`${name}-${i}`} name={name} index={i} />
                ))}
              </motion.div>
            </div>
          </div>

          {/* Muse Perspective */}
          <div className="max-w-4xl mx-auto">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
              <motion.p variants={fadeInUp} className="text-xs tracking-widest uppercase text-background/40 mb-6">How Muse Delivered</motion.p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {DELIVERABLES.map((d, i) => (
                  <motion.div
                    key={d}
                    initial={{ opacity: 0, x: -15 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.08 }}
                    className="flex items-center gap-3 py-3 border-b border-background/10"
                  >
                    <span className="w-2 h-2 rounded-full bg-butter flex-shrink-0" />
                    <span className="text-background/70 text-sm md:text-base">{d}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══════ CAMPAIGN GALLERY ═══════ */}
      <MBGallery />

      {/* ═══════ QUOTE ═══════ */}
      <section className="py-28 md:py-36" data-testid="mb-quote">
        <div className="container-main text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <p className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium leading-tight italic">
              "Great campaigns don't just earn attention. They change behavior."
            </p>
          </motion.div>
        </div>
      </section>

      <div className="border-t border-border" />

      {/* ═══════ NEXT / MORE CASE STUDIES ═══════ */}
      <section id="mb-next" className="py-28 md:py-36" data-testid="mb-next">
        <div className="container-main">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-16">
            <motion.h2 variants={fadeInUp} className="font-serif text-3xl sm:text-4xl font-medium">Explore More Case Studies</motion.h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              { slug: 'aroma360', name: 'Hotel Collection (Aroma360)', cat: 'Influencer Marketing', img: '/images/hc-hero.jpg' },
              { slug: 'its-a-10-haircare', name: "It's a 10 Haircare", cat: 'Social Media', img: '/images/itsa10-hero.jpg' },
              { slug: 'khadija-hallouty', name: 'Khadija Hallouty', cat: 'Personal Brand', img: '/images/khadija-hero.jpg' },
            ].map((p, i) => (
              <motion.div
                key={p.slug}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.6 }}
              >
                <Link to={`/case-studies/${p.slug}`} className="group block">
                  <div className="aspect-[3/4] overflow-hidden mb-4">
                    <img
                      src={p.img}
                      alt={p.name}
                      className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                      loading="lazy"
                    />
                  </div>
                  <p className="text-xs tracking-widest uppercase text-muted-foreground mb-1">{p.cat}</p>
                  <h3 className="font-serif text-xl font-medium group-hover:text-butter-dark transition-colors duration-300">{p.name}</h3>
                </Link>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-16">
            <Link
              to="/case-studies"
              className="inline-flex items-center gap-2 bg-butter text-foreground font-medium text-sm px-10 py-4 rounded-full hover:bg-butter-dark transition-colors duration-300"
            >
              View More
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
